import { useEffect, type RefObject } from 'react';
import { hasFinePointer } from '@/lib/motion';

// Is the page scrolling? The bento's answer (round 4 critique, 2026-09-25).
//
// The first wheel scroll into the bento stalled for 1.2-1.9 s on a cold GPU
// shader cache, then ran at ~25 fps warm. A GPU trace of that scroll put the
// worst frame on the first frame of the first card clip: the compositor has
// to build the program that draws a decoded video frame (one compile of
// 0.6-1.4 s on the test machine) and nothing is drawn meanwhile. Most of the
// rest was the cards' first raster, plus the hover effects (tilt layer, ring,
// shadow, glare) of each card that slid under a resting mouse pointer: about
// a third of the ~100 shader programs built during that scroll.
//
// So nothing optional starts while the page moves: a clip takes its first
// frame once the scroll has settled (CardClip), and with a mouse the cards
// don't take hover until the page stops or the mouse itself moves
// (useScrollCalm).
//
// "Settled" is counted in frames, not just in time since the last scroll
// event. A long frame has no scroll events in it either, so a plain timer
// read the middle of a stall as a pause and started a clip right inside the
// scroll. Here the page has to show CALM_FRAMES frames in a row at the same
// scroll position, spanning at least CALM_MS, before it counts as still.

/** Consecutive frames at one scroll position before the page counts as still. */
const CALM_FRAMES = 8;
/** ...spanning at least this long since the last scroll event. */
const CALM_MS = 180;

type Listener = (scrolling: boolean) => void;

let scrolling = false;
let lastEvent = 0;
let lastY = 0;
let calmFrames = 0;
let raf = 0;
let watchers = 0;
const listeners = new Set<Listener>();
const waiters = new Set<() => void>();

function settle() {
  scrolling = false;
  raf = 0;
  listeners.forEach((l) => l(false));
  const due = Array.from(waiters);
  waiters.clear();
  due.forEach((fn) => fn());
}

function tick() {
  const y = window.scrollY;
  if (y !== lastY) {
    lastY = y;
    calmFrames = 0;
  } else {
    calmFrames += 1;
  }
  if (calmFrames >= CALM_FRAMES && performance.now() - lastEvent >= CALM_MS) {
    settle();
    return;
  }
  raf = window.requestAnimationFrame(tick);
}

function onScroll() {
  lastEvent = performance.now();
  calmFrames = 0;
  if (scrolling) return;
  scrolling = true;
  lastY = window.scrollY;
  listeners.forEach((l) => l(true));
  raf = window.requestAnimationFrame(tick);
}

/** Starts watching the page scroll; the returned function stops (counted). */
function watchScroll(): () => void {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    return () => {};
  }
  watchers += 1;
  if (watchers === 1) window.addEventListener('scroll', onScroll, { passive: true });
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    watchers -= 1;
    if (watchers > 0) return;
    window.removeEventListener('scroll', onScroll);
    if (raf) window.cancelAnimationFrame(raf);
    raf = 0;
    scrolling = false;
    // Whoever still waits belongs to the bento that just unmounted.
    waiters.clear();
  };
}

/** True while the page is scrolling (always false when nothing watches). */
export function isPageScrolling(): boolean {
  return scrolling;
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

/** Runs `fn` when the main thread is idle, or after `timeout` ms at the
 *  latest (Safari has no requestIdleCallback: a short timer stands in).
 *  Returns the cancel. */
export function whenIdle(fn: () => void, timeout = 2500): () => void {
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    const id = w.requestIdleCallback(fn, { timeout });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(fn, Math.min(400, timeout));
  return () => window.clearTimeout(id);
}

/**
 * Runs `fn` once the page has settled: straight away when it is not
 * scrolling. Returns the cancel.
 */
export function afterScrollSettles(fn: () => void): () => void {
  if (!scrolling) {
    fn();
    return () => {};
  }
  const once = () => fn();
  waiters.add(once);
  return () => { waiters.delete(once); };
}

/**
 * Watches the page scroll for as long as the bento is mounted, and with a
 * mouse marks `root` data-scrolling while the page moves: every hover style
 * of the cards is written behind that attribute (ToolChooser.css) and
 * useTilt checks it, so a card that slides under a resting pointer does not
 * start its hover (tilt layer, orbit ring, glare) in the middle of the
 * scroll. The pointer's own movement lifts it at once: a visitor who is
 * aiming at a card gets it immediately. Clicks are never touched: the cards
 * keep their pointer events throughout (round 5b critique, 2026-09-25: with
 * them switched off, a click made within ~0.3 s of a wheel scroll without
 * moving the mouse fell through to the grid and the pick was lost). Touch
 * screens have no hover, so they are left alone.
 */
export function useScrollCalm(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const stop = watchScroll();
    const el = root.current;
    if (!el || !hasFinePointer()) return stop;
    let held = false;
    const hold = (on: boolean) => {
      if (on === held) return;
      held = on;
      if (on) el.setAttribute('data-scrolling', '');
      else el.removeAttribute('data-scrolling');
    };
    const onMove = (e: PointerEvent) => {
      if (held && e.pointerType === 'mouse' && (e.movementX || e.movementY)) hold(false);
    };
    listeners.add(hold);
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      listeners.delete(hold);
      window.removeEventListener('pointermove', onMove);
      hold(false);
      stop();
    };
  }, [root]);
}
